import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';

const CreditHoursChart = ({ rotations }) => {
  // Group rotations by month and calculate total credit hours
  const getMonthlyData = () => {
    const monthlyData = {};
    
    rotations.forEach(rotation => {
      const date = rotation.rotation_start_date || rotation.start_date;
      if (!date) return;
      
      const monthKey = date.substring(0, 7); // YYYY-MM
      const creditHours = rotation.total_credit_hours || 0;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          month: monthKey,
          creditHours: 0,
          count: 0
        };
      }
      
      monthlyData[monthKey].creditHours += creditHours;
      monthlyData[monthKey].count += 1;
    });
    
    // Convert to array and sort by month
    const dataArray = Object.values(monthlyData)
      .sort((a, b) => a.month.localeCompare(b.month))
      .slice(-12); // Last 12 months
    
    // Format month labels and determine color based on threshold
    return dataArray.map(item => ({
      ...item,
      monthLabel: new Date(item.month + '-01').toLocaleDateString('en-US', { 
        month: 'short', 
        year: '2-digit' 
      }),
      // Color green if >= 75 hours, yellow if >= 50, red if < 50
      color: item.creditHours >= 75 ? 'hsl(var(--success))' : 
             item.creditHours >= 50 ? 'hsl(var(--warning))' : 
             'hsl(var(--error))'
    }));
  };

  const data = getMonthlyData();

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-card border border-accent-cyan/20 rounded-lg p-3 shadow-lg">
          <p className="text-sm font-semibold text-foreground mb-1">{data.monthLabel}</p>
          <p className="text-sm text-accent-cyan">
            <strong>{data.creditHours.toFixed(1)}</strong> credit hours
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {data.count} rotation{data.count !== 1 ? 's' : ''}
          </p>
        </div>
      );
    }
    return null;
  };

  if (data.length === 0) {
    return (
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Credit Hours by Month</h3>
        </div>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Icon name="BarChart3" className="w-12 h-12 text-muted-foreground opacity-50 mb-3" />
          <p className="text-sm text-muted-foreground">
            No data available for credit hours chart
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">Credit Hours by Month</h3>
          <p className="text-sm text-muted-foreground">Monthly credit hour totals (color-coded by threshold)</p>
        </div>
        <div className="w-10 h-10 rounded-full bg-accent-cyan/10 flex items-center justify-center">
          <Icon name="BarChart3" className="w-5 h-5 text-accent-cyan" />
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-4 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-success"></div>
          <span className="text-muted-foreground">≥ 75 hours</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-warning"></div>
          <span className="text-muted-foreground">50-74 hours</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-error"></div>
          <span className="text-muted-foreground">&lt; 50 hours</span>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--accent-cyan) / 0.1)" />
          <XAxis 
            dataKey="monthLabel" 
            stroke="hsl(var(--muted-foreground))"
            style={{ fontSize: '0.75rem' }}
          />
          <YAxis 
            stroke="hsl(var(--muted-foreground))"
            style={{ fontSize: '0.75rem' }}
            label={{ 
              value: 'Credit Hours', 
              angle: -90, 
              position: 'insideLeft',
              style: { fill: 'hsl(var(--muted-foreground))', fontSize: '0.75rem' }
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="creditHours" name="Credit Hours" radius={[4, 4, 0, 0]}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CreditHoursChart;
